/**
 * Utilities used by the Learning Time and BigBingo APIs.
 */
import {encodeParams, type Encodable} from "@khan/data-fetch";
import Log, {Errors} from "@khan/logging";

import * as enums from "./enums.ts";
import type {
    LearningTimeContent,
    LearningTimeExplicitClassroomContext,
    LearningTimeInfo_ServerFormat,
    LearningTimeTask,
    LearningTimeTestPrep,
    PartialLearningTimeInfo,
    QuizType,
} from "./types.ts";

/**
 * Performs a nested update of PartialLearningTimeInfo annotations.
 */
export function updateLearningTimeInfo(
    prev: PartialLearningTimeInfo,
    update: PartialLearningTimeInfo,
): PartialLearningTimeInfo {
    const {
        content: updateContent,
        testPrep: updateTestPrep,
        task: updateTask,
        explicitClassroomContext: updateExplicitClassroomContext,
        ...shallow
    } = update;

    const {
        content: prevContent,
        testPrep: prevTestPrep,
        task: prevTask,
        explicitClassroomContext: prevExplicitClassroomContext,
        ...prevShallow
    } = prev;

    const deepUpdates = {
        ...(updateLearningTimeContent(prevContent, updateContent) as any),
        ...(updateLearningTimeTestPrep(prevTestPrep, updateTestPrep) as any),
        ...(updateLearningTimeTask(prevTask, updateTask) as any),
        ...(updateLearningTimeExplicitClassroomContext(
            prevExplicitClassroomContext,
            updateExplicitClassroomContext,
        ) as any),
    } as const;

    return {
        ...prevShallow,
        ...shallow,
        ...deepUpdates,
    };
}

/**
 * Performs a shallow update only.
 */
const updateLearningTimeContent = (
    prev?: Partial<LearningTimeContent> | null,
    update?: Partial<LearningTimeContent> | null,
):
    | {
          content: Partial<LearningTimeContent>;
      }
    | null
    | undefined => {
    const updated = shallowUpdater(prev, update);
    return updated ? {content: updated} : null;
};

/**
 * Performs a shallow update only.
 */
const updateLearningTimeTestPrep = (
    prev?: Partial<LearningTimeTestPrep> | null,
    update?: Partial<LearningTimeTestPrep> | null,
):
    | {
          testPrep: Partial<LearningTimeTestPrep>;
      }
    | null
    | undefined => {
    const updated = shallowUpdater(prev, update);
    return updated ? {testPrep: updated} : null;
};

/**
 * Performs a shallow update only.
 */
const updateLearningTimeTask = (
    prev?: Partial<LearningTimeTask> | null,
    update?: Partial<LearningTimeTask> | null,
):
    | {
          task: Partial<LearningTimeTask>;
      }
    | null
    | undefined => {
    const updated = shallowUpdater(prev, update);
    return updated ? {task: updated} : null;
};

/**
 * Performs a shallow update only.
 */
const updateLearningTimeExplicitClassroomContext = (
    prev?: Partial<LearningTimeExplicitClassroomContext> | null,
    update?: Partial<LearningTimeExplicitClassroomContext> | null,
):
    | {
          explicitClassroomContext: Partial<LearningTimeExplicitClassroomContext>;
      }
    | null
    | undefined => {
    const updated = shallowUpdater(prev, update);
    return updated ? {explicitClassroomContext: updated} : null;
};

function shallowUpdater<T extends Record<any, any>>(
    prev?: T | null,
    update?: T | null,
): T | null | undefined {
    if (prev && update) {
        return {
            ...prev,
            ...update,
        };
    }
    if (prev) {
        return prev;
    }
    if (update) {
        return update;
    }
    return null;
}

/**
 * Associates a ContentKind with an activity.
 */
export const mapContentKindToActivity = (
    kind: enums.LearningTimeContentKind,
): enums.LearningTimeActivity => {
    const {Activity} = enums;
    switch (kind) {
        case "Topic":
            return Activity.NAVIGATING;
        case "Article":
            return Activity.READING;
        case "Video":
        case "Talkthrough":
            return Activity.WATCHING;
        case "Challenge":
        case "Project":
            return Activity.PROGRAMMING;
        case "Exercise":
        case "TopicQuiz":
        case "TopicUnitTest":
        case "CourseChallenge":
        case "MasteryChallenge":
            return Activity.PRACTICING;
        case "Interactive":
            return Activity.OTHER_LEARNING;
        case "AIGuideActivity":
            return Activity.AI_GUIDE_ACTIVITY;
        default:
            const _: never = kind;
            return Activity.OTHER_ACTIVITY;
    }
};

/**
 * Checks to see if the url contains a test-prep subject
 * If the domain is "test-prep", this returns the subject,
 * otherwise returns null.
 *
 * example: "/test-prep/sat/" => "sat"
 */
export const maybeGetTestPrepSubject = (
    url: string,
): string | null | undefined => {
    const urlComponents = url.split("/");
    if (urlComponents.length >= 3) {
        const domain = urlComponents[1];
        const subject = urlComponents[2];

        if (domain === "test-prep") {
            return subject;
        }
    }

    return null;
};

interface TestPrepStruct {
    exam?: enums.LearningTimeExam;
    examGroup?: enums.LearningTimeExamGroup;
    areas?: Array<enums.LearningTimeArea>;
}

/**
 * Creates a TestPrep struct based on the subject.
 * If the subject is not a valid exam type, createTestPrepStruct returns null.
 */
export const createTestPrepStruct = (
    subject: string,
    areaId?: string | null,
): TestPrepStruct | null | undefined => {
    // mapSubjectToLearningTimeExam returns null if subject is not an exam type
    // or a course in an exam
    const exam = mapSubjectToLearningTimeExam(subject);
    const examGroup = exam ? mapExamToExamGroup(exam) : null;
    const area = mapAreaIdToLearningTimeArea(areaId);

    if (!exam && !examGroup) {
        // Avoid defining an empty testPrep struct
        return null;
    }

    const testPrep: TestPrepStruct = {};
    if (exam) {
        testPrep.exam = exam;
    }
    if (examGroup) {
        testPrep.examGroup = examGroup;
    }
    if (area) {
        testPrep.areas = [area];
    }

    return testPrep;
};

/**
 * Maps a string to a ContentKind recognized by the Learning Time backend,
 * otherwise null.
 */
export const mapKindToContentKind = (
    uncheckedKind: string,
): enums.LearningTimeContentKind | null | undefined => {
    const {ContentKind} = enums;
    switch (uncheckedKind) {
        case "Exercise":
            return ContentKind.Exercise;
        case "Article":
            return ContentKind.Article;
        case "Video":
            return ContentKind.Video;

        // Scratchpad types
        case "Challenge":
            return ContentKind.Challenge;
        case "Talkthrough":
            return ContentKind.Talkthrough;
        case "Project":
            return ContentKind.Project;
        case "Interactive":
            return ContentKind.Interactive;

        // Other
        case "Topic":
            return ContentKind.Topic;
        case "TopicQuiz":
            return ContentKind.TopicQuiz;
        case "TopicUnitTest":
            return ContentKind.TopicUnitTest;
        case "CourseChallenge":
            return ContentKind.CourseChallenge;
        case "MasteryChallenge":
            return ContentKind.MasteryChallenge;
        case "AIGuideActivity":
            return ContentKind.AIGuideActivity;

        default:
            return null;
    }
};

export const mapExamToLearningTimeExam = (
    uncheckedExam: string,
): enums.LearningTimeExam | null | undefined => {
    const {Exam} = enums;
    switch (uncheckedExam) {
        case "sat":
            return Exam.SAT;
        case "digital-sat":
            return Exam.DIGITAL_SAT;
        case "lsat":
            return Exam.LSAT;
        case "praxis-math":
            return Exam.PRAXIS_MATH;
        case "praxis-reading":
            return Exam.PRAXIS_READING;
        case "praxis-writing":
            return Exam.PRAXIS_WRITING;
        // These are in fact exams, and fall under the `test-prep` domain but
        // we do not currently have a test prep product for them. We only want
        // to include the `exam` for valid test prep products. However, it isn't
        // an error for this function to receive these values so we allow it to
        // return `null` silently.
        case "mcat":
        case "gmat":
        case "iit-jee-subject":
        case "nclex-rn":
        case "cahsee-subject":
        case "oecd-pisa":
        case "get-ready-for-sat-prep-reading-and-writing":
        case "get-ready-for-sat-prep-math":
        case "":
            return null;
        default:
            // When this happens we are losing valuable learning time data.
            // We'd like to try to minimize/avoid doing this
            // so we are logging to try to track these down.
            Log.error(
                "Expected one of 'sat', 'digital-sat', 'v2-sat-math', 'sat-reading-and-writing', 'sat-reading-and-writing-2022', 'lsat', 'lsat-prep', 'praxis-math', 'praxis-reading', 'praxis-writing' but received unknown exam value",
                Errors.InvalidInput,
                {
                    sentryData: {
                        contexts: {
                            extras: {
                                uncheckedExam,
                            },
                        },
                    },
                },
            );

            // Since `exam` is an optional field, if we cannot match a valid
            // case we return null and intend for the caller to exclude `exam`
            // from the PartialLearningTimeData being constructed (as we do
            // with other optional fields).
            return null;
    }
};

// The primary content for the dSAT exam is not all under
// one course; it is split between v2-sat-math and
// sat-reading-and-writing.
// This function wraps around mapExamToLearningTimeExam
// to return the learning time exam for any subject
// (i.e. course or exam) related to that exam.
export const mapSubjectToLearningTimeExam = (
    uncheckedCourseOrExam: string,
): enums.LearningTimeExam | null | undefined => {
    if (
        uncheckedCourseOrExam.startsWith("dsat--practice-test--") ||
        // These look like "dpsat-practice-test-01-22". So we have to wildcard
        // based on the prefix.
        uncheckedCourseOrExam.startsWith("dpsat-practice-test-") ||
        // This matches slugs like "dsat-practice-test-05-22".
        uncheckedCourseOrExam.startsWith("dsat-practice-test-")
    ) {
        return enums.Exam.DIGITAL_SAT;
    }

    switch (uncheckedCourseOrExam) {
        case "v2-sat-math":
        case "sat-reading-and-writing-2022":
        case "sat-reading-and-writing":
            return enums.Exam.DIGITAL_SAT;
        case "lsat-prep":
            return enums.Exam.LSAT;
        default:
            return mapExamToLearningTimeExam(uncheckedCourseOrExam);
    }
};

/**
 * Maps an exam to an examGroup.
 * ACHTUNG(jvoll): This should only be used for older code where it is
 * difficult to obtain the `examGroup`. In most cases the server should be
 * providing this and mapping should not be necessary.
 */
export const mapExamToExamGroup = (
    exam: enums.LearningTimeExam,
): enums.LearningTimeExamGroup => {
    switch (exam) {
        case "PRAXIS_MATH":
        case "PRAXIS_READING":
        case "PRAXIS_WRITING":
            return enums.ExamGroup.PRAXIS_GROUP;
        case "LSAT":
            return enums.ExamGroup.LSAT_GROUP;
        case "SAT":
        case "DIGITAL_SAT":
            return enums.ExamGroup.SAT_GROUP;
        default:
            const _: never = exam;
            return enums.ExamGroup.INVALID_EXAM_GROUP;
    }
};

/**
 * Maps an InterspersedQuizT to a ContentKind recognized by the LearningTime
 * backend.
 */
export const mapQuizToContentKind = (quiz: {
    type: QuizType;
}): enums.LearningTimeContentKind => {
    switch (quiz.type) {
        case "subject-challenge":
            return enums.ContentKind.CourseChallenge;
        case "test":
            return enums.ContentKind.TopicUnitTest;
        case "mastery-challenge":
            return enums.ContentKind.MasteryChallenge;
        default: {
            // Check for exhaustiveness.
            // NOTE(slim): While it would be more elegant to have a separate
            // `case` statement for "quiz", and do `const _: "quiz" = quiz.type`
            // under the `default` case, we would still need a return value for
            // the `default` branch.
            const _: "quiz" = quiz.type;
            return enums.ContentKind.TopicQuiz;
        }
    }
};

export const mapAreaIdToLearningTimeArea = (
    uncheckedAreaId?: string | null,
): enums.LearningTimeArea | null | undefined => {
    const {Area} = enums;

    // The expected areaId strings can be found by looking at the "SLUG/ID"
    // values in the topics listed here (https://www.khanacademy.org/devadmin/content#x00000000-xaed4beefb9821096-Topic:xa8385ec6201ece74)
    // and removing the corresponding prefix (e.g. "gtp--lsat--"). Not
    // everything listed under there is atually an area and thus if editing
    // this switch statement please seek help from the Test Prep team.
    switch (uncheckedAreaId) {
        // SAT
        case "math":
        case "math-calc":
        case "math-no-calc":
        case "sat-math-practice":
            return Area.MATH;
        case "reading":
        case "reading-and-writing":
        case "writing-and-language":
        case "sat-reading-writing-practice":
            return Area.READING_AND_WRITING;
        case "essay":
        case "new-sat-essay":
            return Area.ESSAY;

        // LSAT
        case "logical-reasoning":
            return Area.LOGICAL_REASONING;
        case "analytical-reasoning":
            return Area.ANALYTICAL_REASONING;
        case "reading-comp":
            return Area.READING_COMP;

        // PRAXIS_MATH
        case "number-and-quantity":
            return Area.NUMBER_AND_QUANTITY;
        case "statistics-and-probability":
            return Area.STATISTICS_AND_PROBABILITY;
        case "algebra":
            return Area.ALGEBRA;
        case "geometry":
            return Area.GEOMETRY;

        // PRAXIS_READING
        case "all-reading-practice":
            return Area.ALL_READING_PRACTICE;

        // PRAXIS_WRITING
        case "all-writing-practice":
            return Area.OTHER_WRITING_PRACTICE;
        case "argumentative-essay":
            return Area.ARGUMENTATIVE_ESSAY;
        case "source-based-essay":
            return Area.SOURCE_BASED_ESSAY;

        // Used for places where the user is doing work on a
        // task that does not have an areaId.
        case "new-sat-tips-planning":
        case "other":
            return Area.OTHER_AREA;

        default:
            // Since `area` is an optional field, if we cannot match a valid
            // case we return null and intend for the caller to exclude `area`
            // from the PartialLearningTimeData being constructed (as we do
            // with other optional fields).
            return null;
    }
};

// This limit was chosen somewhat arbitrarily but the idea is that URLs must be
// < 2048 bytes long. There is a chance that nothing would fall over if we went
// beyond this, however, since this is data being used exclusively for testing,
// it isn't worth risking breaking something (Learning Time!) by letting this
// grow really long.
const SMOKE_TEST_PARAMS_MAX_LENGTH = 500;
const INCLUDED_TEST_KEYS = [
    "activity",
    "product",
    "content.kind",
    "content.domainId",
    "content.courseId",
    "content.unitId",
    "content.lessonId",
    "content.assessmentItemId",
    "content.contentId",
    "testPrep.examGroup",
    "testPrep.exam",
    "testPrep.areas",
    "testPrep.skillIds",
    "task.taskKey",
];

// These are renamed because testPrep.{areas, skillIds} are truncated to one
// entry (the first in the array) when we URL-encode LearningTimeInfo.
const RENAMED_TEST_KEYS: Record<string, string> = {
    "testPrep.areas": "testPrep.firstArea",
    "testPrep.skillIds": "testPrep.firstSkillId",
};

// NOTE(jvoll): This function is used _exclusively_ to support LTv2 smoke
// testing. We are adding a subset of learning time info as URL params to
// `mark_conversions` requests. This allows our smoke tests to assert that
// certain conversions are being fired with the correct learning time info.
// See ADR #208: https://docs.google.com/document/d/14QQ3x3uaJGf_cIValtiGcVv428L61lprjUqw245tu68/edit#heading=h.19s1eaqlee4q
export const getSmokeTestUrlEncodedLearningTimeString = (
    learningTimeInfo?: LearningTimeInfo_ServerFormat | null,
): string | null | undefined => {
    if (!learningTimeInfo) {
        return null;
    }

    const learningTimeInfoTuples: ReadonlyArray<
        [string, Encodable | null | undefined]
    > = flatten(learningTimeInfo)
        .filter(([key, _]: [any, any]) => INCLUDED_TEST_KEYS.indexOf(key) > -1)
        .map(([key, value]: [any, any]) => {
            if (key in RENAMED_TEST_KEYS) {
                return [RENAMED_TEST_KEYS[key], value];
            }
            return [key, value];
        });

    if (learningTimeInfoTuples.length > 0) {
        const encodedParams = encodeParams(learningTimeInfoTuples);
        return encodedParams.length > SMOKE_TEST_PARAMS_MAX_LENGTH
            ? null
            : encodedParams;
    }
    return null;
};

type EncodableCopy = string | number;

/**
 * Flattens an object into an array of [key, value] tuples.
 * Arrays are truncated to the first entry, empty arrays are excluded.
 * For example:
 *
 * input: {
 *     activity: "NAVIGATING",
 *     content: {
 *         kind: "TOPIC",
 *         domainId: "abc123",
 *     },
 *     testPrep: {
 *         areas: ["READING_AND_WRITING", "MATH"],
 *         skillIds: [],
 *     }
 * }
 * output: [
 *     ["activity", "NAVIGATING"],
 *     ["content.kind", "TOPIC"],
 *     ["content.domainId", "abc123"]
 *     ["testPrep.areas", "READING_AND_WRITING"]
 * ]
 */
const flatten = (
    obj: any,
    prefix?: string,
): Array<[string, Nullable<EncodableCopy>]> => {
    let flattened: Array<[string, Nullable<EncodableCopy>]> = [];
    if (!obj) {
        return flattened;
    }

    for (const [key, value] of Object.entries(obj)) {
        const prefixedKey = prefix ? `${prefix}${key}` : key;

        // The isArray check must come before the object check because an
        // array will also pass the object check.
        if (Array.isArray(value)) {
            // Grab first value of the array, do not include empty arrays
            if (value.length > 0) {
                flattened.push([prefixedKey, String(value[0])]);
            }
        } else if (typeof value === "object") {
            flattened = [...flattened, ...flatten(value, `${key}.`)];
        } else {
            flattened.push([prefixedKey, String(value)]);
        }
    }
    return flattened;
};
